import qs from 'qs';
import React, { Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useLocation, useParams } from 'react-router-dom';

// global store
import { useGrowthBook } from '@growthbook/growthbook-react';
import { setScopes } from '../reducers/scopes.reducer';

// utils
import { PubSub, PubSubModule } from '../utils/eventUtils';
import { decodeJwtPayload } from '../utils/jwtUtils';
import {
  cacheAuthTokens,
  reduceScopes,
  retrieveAuthTokens,
} from '../utils/next/auth.utils';

import { STATUS } from '../components/hooks/common/status.statics';
import { useAuth } from '../components/hooks/useAuth';
import { useNavigationScroll } from '../components/hooks/useNavigationScroll';
import { useEnvQuery } from '../queries/useEnvQuery';
import { checkIfMfaEnforced } from '../utils/mfa.utils';

import {
  EVENT_BUS_ACCOUNT_SWITCH_REQUESTED,
  EVENT_BUS_JUMP_REQUESTED,
  JumpEventBus,
} from '../_events/jump.bus';
import {
  getSwitchAccountsToken,
  getTempExchangeTokenForJump,
} from '../api/auth/jump.api';
import { useActor } from '../components/hooks/useActor';
import { useGrowthbookAttrs } from '../config/growthbook/useGrowthbookAttrs.hook';
import { useLogLevels } from '../config/loggers/useLogLevels.hook';
import NotFound from '../common/NotFound';
import { ConsoleLayoutLoading } from '../console/layouts/ConsoleLayoutLoading';
import { initializeConsoleCommands } from '../developers/tools/console-commands';
import { useCowbellTranslations } from '../i18n/translations';
import {
  reyhdrateFromPersona,
  setPlatformLanguage,
  userPersonaDiscovered,
} from '../store/actions';
import { selectUserLanguagePreferences } from '../store/selectors/ui-settings.selectors';
import { exchangePersonaForPath } from '../utils';
import { BrokerRoute } from '../personas/broker/BrokerRoute';
import { usePlatformLanguage } from '../utils/hooks/usePlatformLanguage';
import useNotifications from '../_lib/notifications/useNotifications';
import { useAPIErrorHandler } from '../components/hooks/useAPIErrorHandler';

const CarrierDashboardRoutesWrapper = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'CarrierDashboardRoutesWrapper' */ '../components/wrappers/CarrierDashboardWrapper'
    )
);

const ReinsurerDashboardRoutesWrapper = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'ReinsurerDashboardRoutesWrapper' */ '../components/wrappers/ReinsurerDashboardWrapper'
    )
);

const PremiumProcessorDashboardRoutesWrapper = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'PremiumProcessorDashboardRoutesWrapper' */ '../components/wrappers/PremiumProcessorDashboardWrapper'
    )
);
const VendorDashboardRoutesWrapper = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'CarrierDashboardRoutesWrapper' */ '../components/wrappers/VendorDashboardWrapper'
    )
);

const ClusterDashboardRoutesWrapper = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'ClusterDashboardRoutesWrapper' */ '../components/wrappers/ClusterDashboardWrapper'
    )
);

const CustomerDashboardRoutesWrapper = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'CustomerDashboardRoutesWrapper' */ '../components/wrappers/CustomerDashboardWrapper'
    )
);

const AdminDashboardRoutesWrapper = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'AdminDashboardRoutesWrapper' */ '../components/wrappers/AdminDashboardWrapper'
    )
);

const DaDashboardRoutesWrapper = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'DaDashboardRoutesWrapper' */ '../components/wrappers/DaDashboardWrapper'
    )
);

const MsspDashboardRoutesWrapper = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'MsspDashboardRoutesWrapper' */ '../personas/mssp/MsspDashboardWrapper'
    )
);

const PoolDashboardRoutesWrapper = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'PoolDashboardRoutesWrapper' */ '../components/wrappers/PoolDashboardWrapper'
    )
);

const AgencyDashboardRoutesWrapper = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'AgencyDashboardRoutesWrapper' */ '../components/wrappers/AgencyDashboardWrapper'
    )
);

const MooDashboardRoutesWrapper = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'MooDashboardRoutesWrapper' */ '../pages/moo/MooRoutesWrapper'
    )
);

const AuthBus = new PubSubModule();

export const AuthenticatedRouter = ({ user }: any) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const notifications = useNotifications();
  const actor = useActor();

  // @ts-expect-error TS(2339): Property 'persona' does not exist on type '{}'.
  const { persona: personaFromURL } = useParams();
  const { accountType, isCowbellUserJumped } = useAuth();

  const region = usePlatformLanguage();
  const platformLanguage = useSelector(selectUserLanguagePreferences);
  const { i18n } = useCowbellTranslations();

  useNavigationScroll({
    watchValue: location.pathname,
    restoreTo: (location.state as { restoreScrollY?: number } | undefined)
      ?.restoreScrollY,
  });

  // Sync Redux platformLanguage with region on mount if they differ
  React.useEffect(() => {
    if (region && platformLanguage !== region) {
      dispatch(setPlatformLanguage(region));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Loggers
  useLogLevels();

  // GrowthBook
  const growthbook = useGrowthBook();
  const growthbookAttributes = useGrowthbookAttrs(user);
  // Set specific user info for your FF or test cases here
  React.useEffect(() => {
    growthbook?.setAttributes(growthbookAttributes);
  }, [growthbook, growthbookAttributes]);

  React.useEffect(() => {
    initializeConsoleCommands(dispatch, actor.isCowbell);
  }, [dispatch, actor.isCowbell]);

  React.useEffect(() => {
    if (platformLanguage) {
      i18n.changeLanguage(platformLanguage);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformLanguage]);

  React.useEffect(() => {
    const sub = PubSub.subscribe('api:error:5xx', (data: any) => {
      notifications.error(data.message);
    });

    // sync the store with token information
    dispatch(
      userPersonaDiscovered({
        persona: accountType,
        _legacy_persona: personaFromURL,
      })
    );

    // rehydrate the ui settings
    const uisettings = localStorage.getItem(accountType);

    if (uisettings) {
      const nextUiSettings = JSON.parse(uisettings);
      dispatch(reyhdrateFromPersona(nextUiSettings));
    }

    return () => sub.remove();
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    // set title for application as a tab identifier - PS-2773
    const actorAccName = actor.account.fullName || actor.account.name;
    if (actorAccName) {
      document.title = `${actorAccName} - AI-Native Specialty Insurance Platform`;
    }
  }, [actor]);

  // setup refreshing of access tokens
  useAuthWorker();

  // listen for user jumps
  useJumpListener();

  // listen for scope changes
  const { status: scopesListenerStatus } = useScopesListener();

  // perform check if mfa is enforced
  React.useEffect(() => {
    if (!user || isCowbellUserJumped) {
      return;
    }
    checkIfMfaEnforced(user);
  });

  // fetch env
  useEnvQuery();

  if (
    // @ts-expect-error TS(2367): This condition will always return 'false' since th... Remove this comment to see the full error message
    scopesListenerStatus === STATUS.REJECTED ||
    // @ts-expect-error TS(2367): This condition will always return 'false' since th... Remove this comment to see the full error message
    scopesListenerStatus === STATUS.PENDING ||
    scopesListenerStatus === STATUS.IDLE ||
    i18n.language !== platformLanguage
  ) {
    return null;
  }

  return <PersonaRoutes />;
};

/** Each persona's area, mounted at its base path. */
export const PersonaRoutes = () => {
  return (
    <Switch>
      <Suspense fallback={<ConsoleLayoutLoading />}>
        <Route
          path="/premiumprocessor"
          component={PremiumProcessorDashboardRoutesWrapper}
        />
        <Route path="/reinsurer" component={ReinsurerDashboardRoutesWrapper} />
        <Route path="/carrier" component={CarrierDashboardRoutesWrapper} />
        <Route path="/vendor" component={VendorDashboardRoutesWrapper} />
        <Route path="/cluster" component={ClusterDashboardRoutesWrapper} />
        <Route path="/customer" component={CustomerDashboardRoutesWrapper} />
        <Route path="/agency" component={AgencyDashboardRoutesWrapper} />
        <Route path="/da" component={DaDashboardRoutesWrapper} />
        <Route path="/mssp" component={MsspDashboardRoutesWrapper} />
        <Route path="/pool" component={PoolDashboardRoutesWrapper} />
        {/* The retired franchise area (FRAN-123): no redirects. */}
        <Route path="/franchise" component={NotFound} />
        <Route path="/admin" component={AdminDashboardRoutesWrapper} />
        <Route path="/moo" component={MooDashboardRoutesWrapper} />
        <BrokerRoute />
      </Suspense>
    </Switch>
  );
};

const useAuthWorker = () => {
  React.useEffect(() => {
    // load the auth worker script
    const AuthWorker = new Worker('/js/workers/auth.worker.v2.js');
    const { accessToken, refreshToken } = retrieveAuthTokens();

    // retrieve scopes from access token
    decodeJwtPayload(accessToken, (error: any, payload: any) => {
      if (error) {
        console.error(error);
        return;
      }

      // start the auth worker (refresh polling)
      AuthWorker.postMessage({
        name: 'worker:auth:start',
        payload: {
          accessToken,
          refreshToken,
          expires: payload.exp,
        },
      });
    });

    // when the worker has success or error
    AuthWorker.onmessage = function (message) {
      if (!message.data.isError) {
        const { data } = message;
        cacheAuthTokens(data.payload.accessToken, data.payload.refreshToken);
        AuthBus.publish(
          'AUTHENTICATION:REFRESH:SUCCESSFUL',
          data.payload.accessToken
        );
      }
    };

    // stop auth worker when user logs out
    return () => {
      AuthWorker.terminate();
    };
    // eslint-disable-next-line
  }, []);
};

const useScopesListener = () => {
  const dispatch = useDispatch();
  const tokens = retrieveAuthTokens();
  const { accessToken } = tokens;
  const [status, setStatus] = React.useState(STATUS.IDLE);

  React.useEffect(() => {
    // load in initial scopes if they don't exist
    // @ts-expect-error TS(2345): Argument of type '"PENDING"' is not assignable to ... Remove this comment to see the full error message
    setStatus(STATUS.PENDING);
    decodeJwtPayload(accessToken, (error: any, payload: any) => {
      if (error) {
        // @ts-expect-error TS(2345): Argument of type '"REJECTED"' is not assignable to... Remove this comment to see the full error message
        setStatus(STATUS.REJECTED);
        return;
      }

      // reformat scopes
      const scopes = reduceScopes(payload.p);
      // set scopes in global store
      dispatch(setScopes(scopes));
      // @ts-expect-error TS(2345): Argument of type '"RESOLVED"' is not assignable to... Remove this comment to see the full error message
      setStatus(STATUS.RESOLVED);
    });

    const onRefreshEvent = AuthBus.subscribe(
      'AUTHENTICATION:REFRESH:SUCCESSFUL',
      (nextAccessToken: any) => {
        decodeJwtPayload(nextAccessToken, (error: any, payload: any) => {
          if (error) {
            // @ts-expect-error TS(2345): Argument of type '"REJECTED"' is not assignable to... Remove this comment to see the full error message
            setStatus(STATUS.REJECTED);
            return;
          }

          // reformat scopes

          const scopes = reduceScopes(payload.p);
          // set scopes in global store
          dispatch(setScopes(scopes));
          // @ts-expect-error TS(2345): Argument of type '"RESOLVED"' is not assignable to... Remove this comment to see the full error message
          setStatus(STATUS.RESOLVED);
        });
      }
    );

    return () => {
      onRefreshEvent.remove();
    };
    // eslint-disable-next-line
  }, []);

  return {
    status,
  };
};

export const useJumpListener = () => {
  const handleAPIError = useAPIErrorHandler();

  React.useEffect(() => {
    const onJumpRequestedListener = JumpEventBus.subscribe(
      EVENT_BUS_JUMP_REQUESTED,
      (payload: any) =>
        handleJumpRequested(payload).catch(
          handleAPIError('Unable to jump into this account. Please try again.')
        )
    );

    const onSwitchAccountRequestedListener = JumpEventBus.subscribe(
      EVENT_BUS_ACCOUNT_SWITCH_REQUESTED,
      (payload: any) =>
        handleSwitchAccountRequested(payload).catch(
          handleAPIError('Unable to switch accounts. Please try again.')
        )
    );

    return () => {
      onJumpRequestedListener.remove();
      onSwitchAccountRequestedListener.remove();
    };
  });
};

async function handleJumpRequested({
  accountType,
  accountId,
  accountName,
}: any) {
  const personaPath = exchangePersonaForPath(accountType);

  if (!accountType || !personaPath) {
    throw new Error(`Jump Failed: Unsupported account type "${accountType}"`);
  }

  const { data: token } = await getTempExchangeTokenForJump(
    personaPath,
    accountId
  );

  const { origin } = window;
  const query = accountName
    ? qs.stringify({ accountName }, { addQueryPrefix: true })
    : '';

  window.open(`${origin}/loading/${token}${query}`, '_blank');
}

async function handleSwitchAccountRequested({
  accountType,
  accountId,
  accountName,
}: any) {
  if (!accountType) {
    throw new Error(
      `Account Switch Failed: Unsupported account type "${accountType}"`
    );
  }

  const { data: token } = await getSwitchAccountsToken(accountId);

  const { origin } = window;
  const query = accountName
    ? qs.stringify({ accountName }, { addQueryPrefix: true })
    : '';

  window.open(`${origin}/loading/${token}${query}`, '_blank');
}
